import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Types
import type { CampaignBudget } from 'types';
// Import UI Components
import { KeyValue, Popover } from '@geneui/components';
// Import Types
import type { BudgetConfigFormValues } from '../CampaignSteps/AwardStep/types';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { AwardBudgetSetupTypesEnum, BudgetLimitTypeEnum, BudgetPeriodTypeEnum, LimitBehaviorEnum } from 'enums';
// Import Styles
import styles from './BudgetLimitTypePopover.module.scss';
import { BUDGET_BEHAVIOR_LABEL_KEYS, BUDGET_PERIOD_LABEL_KEYS } from 'constants/budget';

function getLimitTypeFromBudget(
    budget?: Nullable<BudgetConfigFormValues>,
    savedBudget?: Nullable<CampaignBudget>,
): AwardBudgetSetupTypesEnum {
    if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.NoLimit) {
        return AwardBudgetSetupTypesEnum.NoLimit;
    }
    if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
        return AwardBudgetSetupTypesEnum.CountLimit;
    }
    if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
        return AwardBudgetSetupTypesEnum.AmountLimit;
    }
    if (savedBudget?.LimitType === BudgetLimitTypeEnum.Count) {
        return AwardBudgetSetupTypesEnum.CountLimit;
    }
    if (savedBudget?.LimitType === BudgetLimitTypeEnum.Amount) {
        return AwardBudgetSetupTypesEnum.AmountLimit;
    }
    return AwardBudgetSetupTypesEnum.NoLimit;
}

interface BudgetDisplaySource {
    limitType: AwardBudgetSetupTypesEnum;
    period: number | null;
    behavior: number | null;
    count: number | null;
    amount: string | null;
}

function getBudgetDisplaySource(
    budget?: Nullable<BudgetConfigFormValues>,
    savedBudget?: Nullable<CampaignBudget>,
): BudgetDisplaySource {
    let limitType: AwardBudgetSetupTypesEnum = AwardBudgetSetupTypesEnum.NoLimit;
    let period: number | null = null;
    let behavior: number | null = null;
    let count: number | null = null;
    let amount: string | null = null;

    if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.NoLimit) {
        limitType = AwardBudgetSetupTypesEnum.NoLimit;
    } else if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
        limitType = AwardBudgetSetupTypesEnum.CountLimit;
        period = budget.budgetPeriod?.[AwardBudgetSetupTypesEnum.CountLimit] ?? null;
        behavior = budget.limitBehavior?.[AwardBudgetSetupTypesEnum.CountLimit] ?? null;
        count = budget.countLimitCount ?? null;
    } else if (budget?.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
        limitType = AwardBudgetSetupTypesEnum.AmountLimit;
        period = budget.budgetPeriod?.[AwardBudgetSetupTypesEnum.AmountLimit] ?? null;
        behavior = budget.limitBehavior?.[AwardBudgetSetupTypesEnum.AmountLimit] ?? null;
        const amt = budget.amountLimitAmount;
        const currency = budget.amountLimitCurrency;
        amount = !isNil(amt) && currency ? `${amt} ${currency}` : !isNil(amt) ? String(amt) : null;
    } else if (savedBudget?.LimitType === BudgetLimitTypeEnum.Count) {
        limitType = AwardBudgetSetupTypesEnum.CountLimit;
        period = savedBudget.Period;
        behavior = savedBudget.Behavior;
        count = savedBudget.LimitValue;
    } else if (savedBudget?.LimitType === BudgetLimitTypeEnum.Amount) {
        limitType = AwardBudgetSetupTypesEnum.AmountLimit;
        period = savedBudget.Period;
        behavior = savedBudget.Behavior;
        amount = savedBudget.CurrencyId
            ? `${savedBudget.LimitValue} ${savedBudget.CurrencyId}`
            : String(savedBudget.LimitValue);
    }

    return { limitType, period, behavior, count, amount };
}

interface Props {
    /** Budget config from form. Takes precedence over savedBudget. */
    budget?: Nullable<BudgetConfigFormValues>;
    /** Budget from API (e.g. campaign details). */
    savedBudget?: Nullable<CampaignBudget>;
}

const BudgetLimitTypeKeyValue: FC<Props> = ({ budget, savedBudget }) => {
    const { t } = useTranslation();

    const value = useMemo(() => {
        const limitType = getLimitTypeFromBudget(budget, savedBudget);
        switch (limitType) {
            case AwardBudgetSetupTypesEnum.CountLimit:
                return t(l.CountLimit);
            case AwardBudgetSetupTypesEnum.AmountLimit:
                return t(l.AmountLimit);
            default:
                return t(l.NoLimit);
        }
    }, [budget, savedBudget, t]);

    const displaySource = useMemo(() => getBudgetDisplaySource(budget, savedBudget), [budget, savedBudget]);

    const budgetPeriodDisplay = useMemo(() => {
        const { period } = displaySource;
        if (isNil(period)) return null;
        return BUDGET_PERIOD_LABEL_KEYS[period as BudgetPeriodTypeEnum] ?? t(l.NA);
    }, [displaySource, t]);

    const limitBehaviorDisplay = useMemo(() => {
        const { behavior } = displaySource;
        if (isNil(behavior)) return null;
        return BUDGET_BEHAVIOR_LABEL_KEYS[behavior as LimitBehaviorEnum] ?? t(l.NA);
    }, [displaySource, t]);

    const hasPopoverContent =
        !isNil(budgetPeriodDisplay) ||
        !isNil(limitBehaviorDisplay) ||
        !isNil(displaySource.count) ||
        !isNil(displaySource.amount);

    const popoverContent = hasPopoverContent ? (
        <div className={styles.popoverContent}>
            {!isNil(budgetPeriodDisplay) && (
                <KeyValue label={t(l.BudgetPeriod)} value={budgetPeriodDisplay} appearance="horizontal" />
            )}
            {!isNil(limitBehaviorDisplay) && (
                <KeyValue label={t(l.LimitBehavior)} value={limitBehaviorDisplay} appearance="horizontal" />
            )}
            {!isNil(displaySource.count) && (
                <KeyValue label={t(l.AwardCount)} value={displaySource.count} appearance="horizontal" />
            )}
            {!isNil(displaySource.amount) && (
                <KeyValue label={t(l.BudgetAmount)} value={displaySource.amount} appearance="horizontal" />
            )}
        </div>
    ) : null;

    if (popoverContent) {
        return (
            <div className="budget-limit-popover-container">
                <span className="budget-limit-label">{t(l.BonusBudget)}</span>
                <Popover extendTargetWidth={false} Content={popoverContent} align="end" position="right">
                    <span className="budget-limit-value budget-limit-value-hyperlink-text">{value}</span>
                </Popover>
            </div>
        );
    }

    return (
        <div className="budget-limit-popover-container">
            <span className="budget-limit-label">{t(l.BonusBudget)}</span>
            <span className="budget-limit-value">{value}</span>
        </div>
    );
};

export default memo(BudgetLimitTypeKeyValue);
